import { Textarea } from '@ordo/ui/textarea'
import { useCallback, useRef } from 'react'

export type TemplateMode = 'plaintext' | 'html'

const SELF_CLOSING_TAGS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'source',
  'track',
  'wbr',
])

function insertText(textarea: HTMLTextAreaElement, text: string) {
  // Use execCommand to preserve undo history
  document.execCommand('insertText', false, text)
  textarea.dispatchEvent(new Event('input', { bubbles: true }))
}

export function TemplateEditor({
  value,
  onChange,
  mode,
}: {
  value: string
  onChange: (value: string) => void
  mode: TemplateMode
}) {
  const ref = useRef<HTMLTextAreaElement>(null)

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const textarea = ref.current
      if (!textarea) {
        return
      }

      // Tab key inserts indentation instead of moving focus
      if (e.key === 'Tab') {
        e.preventDefault()
        const { selectionStart, selectionEnd } = textarea

        if (e.shiftKey) {
          // Shift+Tab: dedent current line(s)
          const lines = textarea.value.split('\n')
          let pos = 0
          let startLine = 0
          let endLine = 0
          for (let i = 0; i < lines.length; i++) {
            if (pos + lines[i].length >= selectionStart && startLine === 0 && i > 0) {
              startLine = i
            }
            if (pos <= selectionEnd) {
              endLine = i
            }
            pos += lines[i].length + 1
          }
          if (startLine === 0) {
            startLine = endLine
          }

          let removed = 0
          for (let i = startLine; i <= endLine; i++) {
            if (lines[i].startsWith('  ')) {
              lines[i] = lines[i].slice(2)
              removed += 2
            } else if (lines[i].startsWith('\t')) {
              lines[i] = lines[i].slice(1)
              removed += 1
            }
          }

          if (removed > 0) {
            const newValue = lines.join('\n')
            // Select all and replace to preserve undo
            textarea.select()
            insertText(textarea, newValue)
            textarea.setSelectionRange(Math.max(0, selectionStart - 2), Math.max(0, selectionEnd - removed))
            onChange(newValue)
          }
        } else {
          insertText(textarea, '  ')
          onChange(textarea.value)
        }
        return
      }

      // Enter key: auto-indent to match previous line
      if (e.key === 'Enter') {
        const { selectionStart } = textarea
        const before = textarea.value.slice(0, selectionStart)
        const currentLine = before.split('\n').pop() ?? ''
        const indent = /^(\s*)/.exec(currentLine)?.[1] ?? ''

        if (indent) {
          e.preventDefault()
          insertText(textarea, '\n' + indent)
          onChange(textarea.value)
        }
        return
      }

      // HTML-specific: auto-close tags
      if (mode === 'html' && e.key === '>') {
        const { selectionStart } = textarea
        const before = textarea.value.slice(0, selectionStart)

        // Match an opening tag like <div, <p, <span class="..."
        const tagMatch = /<(\w+)(?:\s[^>]*)?$/.exec(before)
        if (tagMatch) {
          const tagName = tagMatch[1].toLowerCase()
          if (!SELF_CLOSING_TAGS.has(tagName)) {
            e.preventDefault()
            const closing = `></${tagName}>`
            insertText(textarea, closing)
            // Place cursor between > and </
            const cursorPos = selectionStart + 1
            textarea.setSelectionRange(cursorPos, cursorPos)
            onChange(textarea.value)
            return
          }
        }
      }
    },
    [mode, onChange],
  )

  return (
    <Textarea
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={handleKeyDown}
      spellCheck={false}
      className="min-h-75 flex-1 resize-none font-mono leading-relaxed"
      placeholder={
        mode === 'html' ? '<html>\n  <body>\n    Hello {{ name }}\n  </body>\n</html>' : 'Hello {{ name }}, ...'
      }
    />
  )
}
